/**
 * Select Community Users Modal
 * Modal for searching and selecting users to add to a community with role assignment
 * Uses shadcn Dialog + Checkbox + Avatar
 */

import { ChevronsUpDown, Search, User as UserIcon } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { MultiSelectDropdown } from "@/components/forms/multiSelectDropdown"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import type { CommunityUserWithRole, UserSearchItem } from "@/features/communities/types"
import { searchUsers } from "@/features/profile/services/user.service"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"
import { cn } from "@/lib"

// Helper to convert uppercase to Capital Case
const toCapitalCase = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()

interface CurrentUserInfo {
  id: string
  name: string | null | undefined
  email: string
  username: string | null | undefined
  profilePictureUrl?: string | null
  profilePhotos?: Array<{ isCover: boolean; is_cover_image: boolean; filePath?: string }>
}

interface SelectCommunityUsersModalProps {
  isOpen: boolean
  onClose: () => void
  onAddUsers: (users: CommunityUserWithRole[]) => void
  existingUserIds?: string[]
  communityRoles?: Array<{ id: string; title: string }>
  defaultRoleId?: string
  communityId?: string
  currentUser?: CurrentUserInfo
  ownerRoleId?: string
}

export function SelectCommunityUsersModal({
  isOpen,
  onClose,
  onAddUsers,
  existingUserIds = [],
  communityRoles = [],
  defaultRoleId,
  communityId,
  currentUser,
  ownerRoleId,
}: SelectCommunityUsersModalProps) {
  const defaultRole =
    defaultRoleId || (communityRoles.length > 0 ? communityRoles[0].id : undefined)

  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [users, setUsers] = useState<UserSearchItem[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState<Map<string, CommunityUserWithRole>>(new Map())

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearchQuery("")
      setDebouncedSearch("")
      setUsers([])
      setPage(1)
      setTotalPages(0)

      // Initialize with current user if provided
      if (currentUser && ownerRoleId) {
        const initialSelected = new Map<string, CommunityUserWithRole>()
        initialSelected.set(currentUser.id, {
          userId: currentUser.id,
          name: currentUser.name || null,
          email: currentUser.email,
          username: currentUser.username || null,
          profilePictureUrl:
            currentUser.profilePhotos?.find(p => p.isCover || p.is_cover_image)?.filePath ||
            currentUser.profilePictureUrl ||
            null,
          roleIds: [ownerRoleId],
          roleId: ownerRoleId,
          isCurrentUser: true, // Mark as current user for locking
        })
        setSelectedUsers(initialSelected)
      } else {
        setSelectedUsers(new Map())
      }
    }
  }, [isOpen, currentUser, ownerRoleId])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  // Fetch users
  const fetchUsers = useCallback(
    async (pageNum: number, query: string, isLoadMore = false) => {
      setIsLoading(true)

      try {
        // Pass include_current_user when currentUser is provided
        const result = await searchUsers({
          search: query || undefined,
          page: pageNum,
          limit: 20,
          community_id: communityId,
          include_current_user: currentUser ? true : undefined,
        })

        // Transform users - if currentUser is provided, include them in the list
        let transformedUsers: UserSearchItem[] = result.users.map(user => ({
          id: user.id,
          name: user.name,
          email: user.email,
          username: user.username,
          profilePictureUrl: user.profileMedia?.[0]?.upload?.filePath || user.profilePictureUrl,
        }))

        // If current user is provided and not in the results, add them manually
        if (currentUser && pageNum === 1 && !isLoadMore) {
          const hasCurrentUser = transformedUsers.some(u => u.id === currentUser.id)
          if (!hasCurrentUser) {
            transformedUsers = [
              {
                id: currentUser.id,
                name: currentUser.name || null,
                email: currentUser.email || "",
                username: currentUser.username || null,
                profilePictureUrl:
                  currentUser.profilePhotos?.find(p => p.isCover || p.is_cover_image)?.filePath ||
                  currentUser.profilePictureUrl ||
                  null,
              },
              ...transformedUsers,
            ]
          }
        }

        if (isLoadMore) {
          setUsers(prev => [...prev, ...transformedUsers])
        } else {
          setUsers(transformedUsers)
        }

        setTotalPages(result.totalPages)
        setPage(pageNum)
      } catch (error) {
        console.error("Failed to fetch users:", error)
        if (!isLoadMore) {
          setUsers([])
        }
      } finally {
        setIsLoading(false)
      }
    },
    [communityId, currentUser],
  )

  // Refetch when search query changes
  useEffect(() => {
    if (isOpen) {
      fetchUsers(1, debouncedSearch)
    }
  }, [debouncedSearch, isOpen, fetchUsers])

  // Infinite scroll handler
  const handleLoadMore = useCallback(() => {
    if (!isLoading && page < totalPages) {
      fetchUsers(page + 1, debouncedSearch, true)
    }
  }, [isLoading, page, totalPages, debouncedSearch, fetchUsers])

  const loadMoreRef = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    hasMore: page < totalPages,
    disabled: isLoading,
  })

  const toggleUserSelection = (user: UserSearchItem) => {
    const newSelected = new Map(selectedUsers)
    const userData = newSelected.get(user.id)

    // Check if this is the current user (locked)
    if (userData?.isCurrentUser) {
      return // Cannot deselect current user
    }

    if (newSelected.has(user.id)) {
      newSelected.delete(user.id)
    } else {
      newSelected.set(user.id, {
        userId: user.id,
        name: user.name,
        email: user.email,
        username: user.username,
        profilePictureUrl: user.profilePictureUrl,
        roleIds: defaultRole ? [defaultRole] : [],
        roleId: defaultRole || "",
      })
    }

    setSelectedUsers(newSelected)
  }

  const updateUserRoles = (userId: string, roleIds: string[]) => {
    const newSelected = new Map(selectedUsers)
    const user = newSelected.get(userId)

    // Check if this is the current user (locked)
    if (user?.isCurrentUser) {
      return // Cannot change role for current user
    }

    if (user) {
      newSelected.set(userId, { ...user, roleIds })
      setSelectedUsers(newSelected)
    }
  }

  const handleAddUsers = () => {
    const usersToAdd = Array.from(selectedUsers.values())
    onAddUsers(usersToAdd)
    onClose()
  }

  const isUserExisting = (userId: string) => existingUserIds.includes(userId)

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="w-[600px]  max-h-[80vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-6">
          <DialogTitle className="text-xl font-semibold text-charcoal">Add Members</DialogTitle>
        </DialogHeader>

        {/* Search Bar */}
        <div className="px-5">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone" />
            <Input
              type="text"
              placeholder="Search by name, email, or username..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-0 text-sm focus:outline-none"
            />
          </div>
        </div>

        {/* Users List */}
        <div className="flex-1 overflow-y-auto p-2">
          {isLoading && users.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <UserIcon size={48} className="mx-auto mb-4 text-stone" />
              <p className="text-stone">
                {debouncedSearch ? "No users match your search" : "No users found"}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {users.map(user => {
                const isSelected = selectedUsers.has(user.id)
                const isExisting = isUserExisting(user.id)
                const userData = selectedUsers.get(user.id)
                const isCurrentUser = userData?.isCurrentUser === true

                return (
                  <div
                    key={user.id}
                    className={cn(
                      "flex items-center gap-3 p-3 rounded-md transition-colors",
                      isExisting && !isCurrentUser && "cursor-not-allowed",
                      isCurrentUser && "bg-transparent",
                    )}
                  >
                    {/* Checkbox - disabled for current user */}
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() =>
                        !isExisting && !isCurrentUser && toggleUserSelection(user)
                      }
                      disabled={isExisting || isCurrentUser}
                    />

                    {/* Avatar */}
                    <Avatar
                      src={user.profilePictureUrl || undefined}
                      fallback={user.name || user.email}
                      size="md"
                    />

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-charcoal truncate">
                        {user.name || "No name"}
                      </p>
                      <p className="text-xs text-stone truncate">
                        {user.username ? `@${user.username}` : user.email}
                      </p>
                    </div>

                    {/* Role Multi-Select - Show disabled dropdown for current user (Owner) */}
                    {isSelected &&
                      userData &&
                      (isCurrentUser ? (
                        <div className="font-bold rounded-lg text-[14px] text-primary text-left focus:outline-none flex items-center justify-end gap-3 px-3 py-1.5 bg-black/5 opacity-50 cursor-not-allowed min-w-[140px]">
                          <span className="truncate">Owner</span>
                          <ChevronsUpDown size={16} strokeWidth={2.5} />
                        </div>
                      ) : (
                        <MultiSelectDropdown
                          options={communityRoles
                            .filter(r => r.title !== "OWNER")
                            .map(r => toCapitalCase(r.title))}
                          selectedOptions={
                            userData.roleIds && userData.roleIds.length > 0
                              ? communityRoles
                                  .filter(r => userData.roleIds.includes(r.id))
                                  .map(r => toCapitalCase(r.title))
                              : ["Ritualist"]
                          }
                          onChange={labels => {
                            const selectedRoleIds = communityRoles
                              .filter(r => labels.includes(toCapitalCase(r.title)))
                              .map(r => r.id)
                            updateUserRoles(user.id, selectedRoleIds)
                          }}
                        />
                      ))}

                    {/* Existing Badge - only for non-current users */}
                    {isExisting && !isCurrentUser && (
                      <span className="text-xs text-stone">Already added</span>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Infinite Scroll Sentinel */}
          <div ref={loadMoreRef} className="h-1" />
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-5 pb-5">
          <span className="text-sm text-stone">
            {selectedUsers.size} {selectedUsers.size === 1 ? "user" : "users"} selected
          </span>
          <div className="flex gap-3">
            <Button
              onClick={handleAddUsers}
              disabled={selectedUsers.size === 0}
              className="text-[12px] px-10"
              variant="secondary"
              size="sm"
            >
              Add Selected
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
